import { useTranslation } from 'react-i18next';

import { FilterBar } from '@/components/shared/FilterBar';
import InfiniteSearchSelect from '@/components/shared/InfiniteSearchSelect';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { SearchSelect } from '@/components/ui/daisy';
import { ComplexDateOutputValue } from '@/components/ui/daisy/templates/complex-date-input/types';
import useAuth from '@/data/hook/useAuth';
import useAuthorizedUnits from '@/data/hook/shared/useAuthorizedUnits';
import useScopeFilterChips from '@/data/hook/shared/useScopeFilterChips';
import { PendencyAdvancedFilters } from '@/data/hook/shared/pendencies/usePendencyAdvancedFilters';
import { ActivityPendenciesMainFilters } from '@/data/hook/useActivityPendenciesFilter';
import {
  klassesQueryKeys,
  unitsQueryKeys,
  usersQueryKeys,
} from '@/data/services/querykeys';
import { isAdmin, isTeacher } from '@/functions/auth';
import { handleUserFullName } from '@/functions/handleUserFullName';
import Klass, { KlassStatusEnum } from '@/models/Klass';
import Unit from '@/models/_deprecated/Unit';
import User, { UserStatusEnum, UserTypeEnum } from '@/models/User';
import {
  fromComplexDateValue,
  toComplexDateValue,
} from '@/utils/filters/dateRangeParams';
import { translateKlassName } from '@/utils/translateKlassName';

type ActivityPendenciesFilterBarProps = {
  mainFilters: ActivityPendenciesMainFilters;
  advancedFilters: PendencyAdvancedFilters;
  advancedFiltersCount: number;
  updateMainFilters: (updated: Partial<ActivityPendenciesMainFilters>) => void;
  updateAdvancedFilters: (updated: Partial<PendencyAdvancedFilters>) => void;
  clearAdvancedFilters: () => void;
};

export default function ActivityPendenciesFilterBar({
  mainFilters,
  advancedFilters,
  advancedFiltersCount,
  updateMainFilters,
  updateAdvancedFilters,
  clearAdvancedFilters,
}: ActivityPendenciesFilterBarProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'pendencies' });
  const { t: tActivities } = useTranslation('translation', {
    keyPrefix: 'pendencies.activities.filters',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const { user } = useAuth();
  const userIsTeacher = isTeacher(user?.userType);
  const isAdminUser = isAdmin(user?.userType);
  const { canFilterByUnit, authorizedUnits } = useAuthorizedUnits();
  const hasAdvancedFilters = isAdminUser || canFilterByUnit;

  const chips = useScopeFilterChips(advancedFilters);

  const dateRangeValue = toComplexDateValue(mainFilters);

  const handleDateRangeChange = (value: ComplexDateOutputValue | null) =>
    updateMainFilters(fromComplexDateValue(value));

  const removeChip = (key: string) =>
    updateAdvancedFilters({ [key]: undefined });

  return (
    <FilterBar>
      <FilterBar.Fields>
        <FilterBar.FieldsGroup>
          <FilterBar.Search
            defaultValue={mainFilters.search}
            placeholder={tActivities('searchPlaceholder')}
            onSearch={search => updateMainFilters({ search })}
          />
          <SearchSelect.Fieldset label={tCommon('klass')} className="flex-1">
            <InfiniteSearchSelect
              listService={klassesQueryKeys.list}
              getService={id => klassesQueryKeys.get(Number(id))}
              filters={{
                teacherId: userIsTeacher && user ? [user.id] : undefined,
                unitId: authorizedUnits,
                status: [KlassStatusEnum.OPEN, KlassStatusEnum.IN_PROGRESS],
              }}
              getOptionLabel={(klass: Klass) => translateKlassName(klass.name)}
              value={mainFilters.klassId ?? null}
              onChange={klass => updateMainFilters({ klassId: klass?.id })}
              placeholder={t('filters.all')}
            >
              <SearchSelect.Option value="">
                {t('filters.all')}
              </SearchSelect.Option>
            </InfiniteSearchSelect>
          </SearchSelect.Fieldset>
          <SearchSelect.Fieldset
            label={t('activities.filters.student')}
            className="flex-1"
          >
            <InfiniteSearchSelect
              listService={usersQueryKeys.list}
              getService={id => usersQueryKeys.get(Number(id))}
              filters={{
                userType: [UserTypeEnum.STUDENT],
                status: [UserStatusEnum.ACTIVE_ENROLLMENT],
                unitId: authorizedUnits,
                teacher: userIsTeacher && user ? user.id : undefined,
              }}
              getOptionLabel={(student: User) => handleUserFullName(student)}
              value={mainFilters.studentId ?? null}
              onChange={student =>
                updateMainFilters({ studentId: student?.id })
              }
              placeholder={t('filters.all')}
            >
              <SearchSelect.Option value="">
                {t('filters.all')}
              </SearchSelect.Option>
            </InfiniteSearchSelect>
          </SearchSelect.Fieldset>

          <FilterBar.DateInput
            label={tActivities('periodSend')}
            value={dateRangeValue}
            onChange={handleDateRangeChange}
          />
        </FilterBar.FieldsGroup>
        <ConditionalRenderer condition={hasAdvancedFilters}>
          <FilterBar.MoreFilters count={advancedFiltersCount} />
        </ConditionalRenderer>
      </FilterBar.Fields>

      <FilterBar.Advanced
        className="grid grid-cols-3"
        onClearAll={advancedFiltersCount > 0 ? clearAdvancedFilters : undefined}
      >
        <ConditionalRenderer condition={isAdminUser}>
          <SearchSelect.Fieldset label={tCommon('teacher')} className="flex-1">
            <InfiniteSearchSelect
              listService={usersQueryKeys.list}
              getService={id => usersQueryKeys.get(Number(id))}
              filters={{
                userType: [UserTypeEnum.TEACHER],
                status: [UserStatusEnum.REGISTRED],
                unitId: authorizedUnits,
              }}
              getOptionLabel={(teacher: User) => handleUserFullName(teacher)}
              value={advancedFilters.teacher ?? null}
              onChange={teacher =>
                updateAdvancedFilters({ teacher: teacher?.id })
              }
              placeholder={t('filters.all')}
            >
              <SearchSelect.Option value="">
                {t('filters.all')}
              </SearchSelect.Option>
            </InfiniteSearchSelect>
          </SearchSelect.Fieldset>
        </ConditionalRenderer>

        <ConditionalRenderer condition={canFilterByUnit}>
          <SearchSelect.Fieldset label={tCommon('unit')} className="flex-1">
            <InfiniteSearchSelect
              listService={unitsQueryKeys.list}
              getService={id => unitsQueryKeys.get(Number(id))}
              filters={{ idIn: authorizedUnits?.toString() }}
              getOptionLabel={(unit: Unit) => unit.name}
              value={advancedFilters.unit ?? null}
              onChange={unit => updateAdvancedFilters({ unit: unit?.id })}
              placeholder={t('filters.all')}
            >
              <SearchSelect.Option value="">
                {t('filters.all')}
              </SearchSelect.Option>
            </InfiniteSearchSelect>
          </SearchSelect.Fieldset>
        </ConditionalRenderer>
      </FilterBar.Advanced>

      <FilterBar.Chips
        chips={chips}
        onRemove={removeChip}
        onClearAll={clearAdvancedFilters}
      />
    </FilterBar>
  );
}
